declare global {
  interface Window {
    turnstile: any;
  }
}
'use client';

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  FormControl,
  InputLabel,
} from '@mui/material';
import { countries } from '../utils/countries';

// Import the styled components from JoinForm
import { StyledTextField, StyledSelect, StyledFormControlLabel } from './StyledFormComponents';

const streamingPlatforms = [
  'Spotify', 'Apple Music', 'YouTube Music', 'Tidal', 'Amazon Music', 
  'Deezer', 'SoundCloud', 'Bandcamp', 'Other'
];

const referralSources = [
  'Social Media', 'Friend/Family', 'Artist Recommendation', 'Search Engine',
  'Music Blog', 'Music Forum', 'Other'
];

const FanWaitlistForm = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const turnstileContainerRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);
  
  const [form, setForm] = useState({
    name: '',
    email: '',
    favourite_artist: '',
    streaming_platform: '',
    country: '',
    referral_source: '',
    is_subscribed: true,
  });

  // Initialize Turnstile verification
  useEffect(() => {
    console.log("TURNSTILE_DEBUG: FanWaitlistForm mounted. Waiting for Turnstile API to load...");
    let attemptInterval: ReturnType<typeof setTimeout>;

    const attemptRenderTurnstile = () => {
      if (window.turnstile && turnstileContainerRef.current && !turnstileWidgetIdRef.current) {
        console.log("TURNSTILE_DEBUG: API loaded, container ready, widget not yet rendered. Rendering widget...");
        try {
          const widgetId = window.turnstile.render(turnstileContainerRef.current, {
            sitekey: '0x4AAAAAABfgu43iKR6cfJY3',
            callback: (token: string) => {
              console.log("TURNSTILE_DEBUG: Success callback! Token received:", token ? `[TOKEN_PRESENT - length: ${token.length}]` : "[NO_TOKEN]");
              setTurnstileToken(token);
            },
            'expired-callback': () => {
              console.log("TURNSTILE_DEBUG: Expired callback. Clearing token.");
              setTurnstileToken('');
            },
            'error-callback': (errorCode: any) => {
              console.error("TURNSTILE_DEBUG: Error callback. Error code:", errorCode);
              setTurnstileToken('');
              setError('Verification challenge failed. Please try refreshing the page.');
            },
            'unsupported-callback': () => {
              console.error("TURNSTILE_DEBUG: Unsupported callback. Browser not supported?");
              setError("Verification challenge not supported by your browser.");
            }
          });
          turnstileWidgetIdRef.current = widgetId;
          console.log("TURNSTILE_DEBUG: Widget rendered with ID:", widgetId);
          clearInterval(attemptInterval); // Stop trying once rendered
        } catch (renderError) {
          console.error("TURNSTILE_DEBUG: Error calling window.turnstile.render:", renderError);
          setError('Could not load verification challenge. Please refresh.');
        }
      }
    };

    attemptRenderTurnstile(); // Initial attempt
    attemptInterval = setInterval(attemptRenderTurnstile, 1000); // Retry every second

    return () => {
      console.log("TURNSTILE_DEBUG: FanWaitlistForm unmounting. Cleaning up Turnstile.");
      clearInterval(attemptInterval);
      if (window.turnstile && turnstileWidgetIdRef.current) {
        console.log("TURNSTILE_DEBUG: Removing widget:", turnstileWidgetIdRef.current);
        window.turnstile.remove(turnstileWidgetIdRef.current);
        turnstileWidgetIdRef.current = null;
      }
    };
  }, []);

  // Monitor turnstileToken state
  useEffect(() => {
    console.log("TURNSTILE_DEBUG: turnstileToken state changed to:", turnstileToken ? `[TOKEN_PRESENT - length: ${turnstileToken.length}]` : "[NO_TOKEN]");
  }, [turnstileToken]);

  const handleChange = (field: string, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("TURNSTILE_DEBUG: handleSubmit called.");
    setLoading(true);
    setError('');

    if (!turnstileToken) {
      console.log("TURNSTILE_DEBUG: Submission blocked, no Turnstile token.");
      setError('Please complete the human verification step.');
      setLoading(false);
      return;
    }
    console.log("TURNSTILE_DEBUG: Submitting with token:", turnstileToken);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/public/waitlist/fan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          ...form, 
          cfTurnstileToken: turnstileToken 
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Submission failed');
      }

      setSubmitted(true);
    } catch (err: any) {
      console.error('Submission error:', err);
      setError(err.message || 'Something went wrong.');
      if (err.message?.includes('verification') || err.message?.includes('Turnstile')) {
        console.log("TURNSTILE_DEBUG: Verification error on submit. Clearing token & resetting widget.");
        setTurnstileToken('');
        if (window.turnstile && turnstileWidgetIdRef.current) {
            window.turnstile.reset(turnstileWidgetIdRef.current);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Typography variant="h5" sx={{ mt: 4, fontFamily: '"Poppins", sans-serif', fontWeight: 700, textAlign: 'center' }}>
        🎉 You're on the list! We'll notify you when Lsnto launches.
      </Typography>
    );
  }

  const isButtonDisabled = loading || !turnstileToken;

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <StyledTextField label="Your Name" required value={form.name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('name', e.target.value)} />
      <StyledTextField label="Email" required type="email" value={form.email} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('email', e.target.value)} />
      
      <StyledTextField 
        label="Favourite Independent Artist/Band" 
        value={form.favourite_artist} 
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('favourite_artist', e.target.value)} 
      />
      
      <StyledSelect
        label="Preferred Streaming Platform"
        value={form.streaming_platform}
        onChange={(e: React.ChangeEvent<{ value: unknown }>) => handleChange('streaming_platform', e.target.value)}
        options={streamingPlatforms}
      />
      
      <StyledSelect
        label="Country"
        value={form.country}
        onChange={(e: React.ChangeEvent<{ value: unknown }>) => handleChange('country', e.target.value)}
        options={countries}
      />
      
      <StyledSelect
        label="How did you hear about us?"
        value={form.referral_source}
        onChange={(e: React.ChangeEvent<{ value: unknown }>) => handleChange('referral_source', e.target.value)}
        options={referralSources}
      />
      
      <StyledFormControlLabel
        control={
          <Checkbox
            checked={form.is_subscribed}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('is_subscribed', e.target.checked)}
            size="small"
          />
        }
        label="Keep me updated about Lsnto news and updates"
        sx={{ mt: 1 }}
      />
      
      <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
        <div ref={turnstileContainerRef} />
      </Box>

      {error && <Typography color="error" sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.85rem', mt: 1 }}>{error}</Typography>}

      <Button 
        type="submit" 
        variant="contained" 
        disabled={isButtonDisabled}
        sx={{ 
          mt: 2, 
          py: 1, 
          fontFamily: '"Poppins", sans-serif', 
          fontWeight: 600,
          borderRadius: '8px',
          textTransform: 'none',
          fontSize: '0.95rem',
          boxShadow: '0 4px 12px rgba(255, 0, 84, 0.3)',
          background: 'linear-gradient(45deg, #FF0054, #662d91)',
          '&:hover': {
            boxShadow: '0 6px 14px rgba(255, 0, 84, 0.4)',
          }
        }}
      >
        {loading ? <CircularProgress size={20} color="inherit" /> : 'Join the Waitlist'}
      </Button>
    </Box>
  );
};

export default FanWaitlistForm; 